/**
 * This file limits how many images load at the same time.
 * Gallery and grid views use it before showing a thumbnail, so a screen
 * full of images doesn't ask the browser to download and decode everything
 * at once, which would delay the rest of the page from showing up.
 * The mental model: a small queue holds pending loads, and only a few
 * run together; each finished load frees a slot for the next one in line.
 */
import { preloadImage } from '@/helpers/mediaElements';

const MAX_CONCURRENT = 3;

let active = 0;
const queue: Array<() => void> = [];

/**
 * Loads an image but waits in line if too many loads are already running.
 * Resolves to true when the image loads, false on error — it never rejects.
 */
export function loadImageThrottled(url: string): Promise<boolean> {
  return new Promise(resolve => {
    const start = () => {
      active++;
      preloadImage(url)
        .then(
          () => true,
          () => false
        )
        .then(ok => {
          active--;
          resolve(ok);
          runNext();
        });
    };
    if (active < MAX_CONCURRENT) start();
    else queue.push(start);
  });
}

function runNext(): void {
  if (active >= MAX_CONCURRENT || queue.length === 0) return;
  const next = queue.shift()!;
  next();
}
